import { z } from 'zod'

import { usernameSchema } from 'src/shared/features/users/schemas/user'

import { phoneSchema } from './consumer-auth'

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .regex(/^(?=.*[a-z])/, 'Password must contain at least 1 lowercase letter')
  .regex(/^(?=.*[A-Z])/, 'Password must contain at least 1 uppercase letter')
  .regex(/^(?=.*\d)/, 'Password must contain at least 1 number')
  // .regex(/^(?=.*[^$*.\[\]{}()?!"@#%&/\\,><':;|_~`+=-])/, 'Password must contain at least 1 special character')
  .regex(/^(?=.*[#?!@$%^&*-])/, 'Password must contain at least 1 special character')
  .regex(
    /^[A-Za-z\d^$*.[\]{}()?!"@#%&/\\,><':;|_~`+=-]+$/,
    'Password can only contain letters, numbers, and these special characters: ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < \' : ; | _ ~ ` + = (no spaces)',
  )
  .refine((val) => !val.includes(' '), 'Password cannot contain spaces')

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: passwordSchema,
})

export const signupSchema = loginSchema
  .extend({
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const requestPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

export const resetPasswordSchema = z.object({
  code: z.string(),
  newPassword: passwordSchema,
  confirmPassword: passwordSchema,
})

export const updatePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export type LoginFormData = z.infer<typeof loginSchema>
export type SignupFormData = z.infer<typeof signupSchema>
export type RequestPasswordFormData = z.infer<typeof requestPasswordSchema>
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>
export type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>

// ------------------------------------------------------------------------------------------------
// Enterprise auth schemas
// ------------------------------------------------------------------------------------------------

export const enterpriseWelcomeSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

export const enterpriseLoginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(10, 'Password must be at least 10 characters'),
})

export const enterpriseSignupSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    password: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const enterpriseCreateAccountSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const enterpriseVerifyEmailSchema = z.object({
  code: z.string().regex(/^[0-9]{6}$/, 'Verification code must be 6 digits'),
})

export type EnterpriseWelcomeFormData = z.infer<typeof enterpriseWelcomeSchema>
export type EnterpriseLoginFormData = z.infer<typeof enterpriseLoginSchema>
export type EnterpriseSignupFormData = z.infer<typeof enterpriseSignupSchema>
export type EnterpriseCreateAccountFormData = z.infer<typeof enterpriseCreateAccountSchema>
export type EnterpriseVerifyEmailFormData = z.infer<typeof enterpriseVerifyEmailSchema>

// ------------------------------------------------------------------------------------------------
// Enterprise onboarding schemas
// ------------------------------------------------------------------------------------------------

export const enterpriseAboutYouSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  username: usernameSchema,
  phoneNumber: phoneSchema,
})

export type EnterpriseCompanySizeValues =
  | '1-10'
  | '11-50'
  | '51-200'
  | '201-500'
  | '501-1000'
  | '1001-5000'
  | '5001-10000'
  | '10001+'

export const enterpriseCompanySizeMap: Record<EnterpriseCompanySizeValues, { label: string }> = {
  '1-10': { label: '1-10 employees' },
  '11-50': { label: '11-50 employees' },
  '51-200': { label: '51-200 employees' },
  '201-500': { label: '201-500 employees' },
  '501-1000': { label: '501-1,000 employees' },
  '1001-5000': { label: '1,001-5,000 employees' },
  '5001-10000': { label: '5,001-10,000 employees' },
  '10001+': { label: '10,001+ employees' },
}

// TEMP VALUES FOR COMPANY TYPE
export type EnterpriseCompanyTypeValues = 'llc' | 'corporation' | 'partnership' | 'sole-proprietorship'

export const enterpriseCompanyInfoSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  companySize: z.enum(['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001-10000', '10001+']),
  companyType: z.enum(['llc', 'corporation', 'partnership', 'sole-proprietorship']),
  companyRole: z.string().min(1, 'Company role is required'),
})

// TEMP VALUES FOR FOCUS
export type EnterpriseFocusValues =
  | 'Feature #1'
  | 'Feature #2'
  | 'Feature #3'
  | 'Feature #4'
  | 'Feature #5'
  | 'Feature #6'

export const enterpriseFocusSchema = z.object({
  focus: z
    .array(z.enum(['Feature #1', 'Feature #2', 'Feature #3', 'Feature #4', 'Feature #5', 'Feature #6']))
    .max(2, 'You can select up to at most 2 focus options'),
})

export type EnterpriseAboutYouFormData = z.infer<typeof enterpriseAboutYouSchema>
export type EnterpriseCompanyInfoFormData = z.infer<typeof enterpriseCompanyInfoSchema>
export type EnterpriseFocusFormData = z.infer<typeof enterpriseFocusSchema>
